import type { HistoryQuery, HistoryResult, PruneBatch, PruneBatchResult } from "../../../server/src/opencode/session-pruning/history-contract"
import type { GitHistoryPage, GitCommitDetails, GitCommitDiff } from "../../../server/src/api-types"
import type { NavigationTarget, NavigationWindowResult, OutlineResult, OutlinePreviewResult, OutlineCheckpoint } from "../../../server/src/opencode/session-pruning/navigation-contract"
import type {
  PruneRequest,
  PermissionReceiptPage,
  PruneResult,
  BinaryValidationResult,
  ConfigFileContentRequest,
  ConfigFileContentResponse,
  ConfigFileListResponse,
  FileSystemEntry,
  FileSystemCreateFolderResponse,
  FileSystemFileContentResponse,
  FileSystemListResponse,
  InstanceData,
  OpenCodeUpdateResponse,
  OpenCodeUpdateStatus,
  SpeechCapabilitiesResponse,
  SpeechSynthesisResponse,
  SpeechTranscriptionResponse,
  SideCar,
  PreviewSession,
  PluginActivationMutationRequest,
  PluginActivationMutationResponse,
  PluginControlLocation,
  PluginControlsSnapshot,
  ProviderUsageResponse,
  ProviderAccountsSnapshot,
  ServerMeta,
  RemoteProxySessionCreateRequest,
  RemoteProxySessionCreateResponse,
  RemoteServerProbeRequest,
  RemoteServerProbeResponse,
  YoloStateResponse,
  WorkspaceCloneRequest,
  WorkspaceCloneResponse,
  WorktreeGitCommitRequest,
  WorktreeGitCommitResponse,
  WorktreeGitDiffRequest,
  WorktreeGitMutationResponse,
  WorktreeGitPathsRequest,
  WorkspaceCreateRequest,
  WorkspaceCreateResponse,
  WorkspaceDescriptor,
  WorkspacePendingRequestsResponse,
  WorkspaceFileResponse,
  WorkspaceFileSearchResponse,

  WorkspaceLogEntry,
  WorkspaceEventPayload,
  WorkspaceEventType,
  WorktreeListResponse,
  WorktreeCreateRequest,
  WorktreeSessionMoveRequest,
  WorktreeSessionMoveResponse,
  WorktreeGitDiffResponse,
  WorktreeGitStatusResponse,
} from "../../../server/src/api-types"
import { getClientIdentity } from "./client-identity"
import { getLogger } from "./logger"
import { attachEventSourceHandlers } from "./event-source-handlers"
import { HttpResponseError, retryFileSearch } from "./retryable-file-search"
import { authenticatedFetch } from "./auth-recovery"
import { CODENOMAD_API_BASE as API_BASE } from "./api-base"

const DEFAULT_EVENTS_PATH = typeof window !== "undefined" ? window.__CODENOMAD_EVENTS_URL__ ?? "/api/events" : "/api/events"
const EVENTS_URL = buildEventsUrl(API_BASE, DEFAULT_EVENTS_PATH)

export const CODENOMAD_API_BASE = API_BASE

function buildEventsUrl(base: string | undefined, path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path
  }
  if (base) {
    const normalized = path.startsWith("/") ? path : `/${path}`
    return `${base}${normalized}`
  }
  return path
}

const httpLogger = getLogger("api")
const sseLogger = getLogger("sse")

function normalizeHeaders(headers: HeadersInit | undefined): Record<string, string> {
  const output: Record<string, string> = {}
  if (!headers) return output

  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      output[key] = value
    })
    return output
  }

  if (Array.isArray(headers)) {
    for (const [key, value] of headers) {
      output[key] = value
    }
    return output
  }

  return { ...headers }
}

function logHttp(message: string, context?: Record<string, unknown>) {
  if (context) {
    httpLogger.info(message, context)
    return
  }
  httpLogger.info(message)
}

async function readErrorMessage(response: Response): Promise<string> {
  const text = await response.text()
  if (!text) return `Request failed with ${response.status}`

  try {
    const parsed = JSON.parse(text) as { error?: unknown; message?: unknown }
    if (typeof parsed?.error === "string" && parsed.error.trim()) {
      return parsed.error
    }
    if (typeof parsed?.message === "string" && parsed.message.trim()) {
      return parsed.message
    }
  } catch {
    // Keep the original body for plain-text responses.
  }

  return text
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const url = API_BASE ? new URL(path, API_BASE).toString() : path
  const headers = normalizeHeaders(init?.headers)
  if (init?.body !== undefined) {
    headers["Content-Type"] = "application/json"
  }

  const method = (init?.method ?? "GET").toUpperCase()
  const startedAt = Date.now()
  logHttp(`${method} ${path}`)

  try {
    const response = await authenticatedFetch(url, { ...init, headers, credentials: init?.credentials ?? "include" })
    if (!response.ok) {
      const message = await readErrorMessage(response)
      logHttp(`${method} ${path} -> ${response.status}`, { durationMs: Date.now() - startedAt, error: message })
      throw new HttpResponseError(message || `Request failed with ${response.status}`, response.status, response.headers.get("Retry-After"))
    }
    const duration = Date.now() - startedAt
    logHttp(`${method} ${path} -> ${response.status}`, { durationMs: duration })
    if (response.status === 204) {
      return undefined as T
    }
    return (await response.json()) as T
  } catch (error) {
    logHttp(`${method} ${path} failed`, { durationMs: Date.now() - startedAt, error })
    throw error
  }
}

async function requestRaw(path: string, init?: RequestInit): Promise<Response> {
  const url = API_BASE ? new URL(path, API_BASE).toString() : path
  const headers = normalizeHeaders(init?.headers)
  if (init?.body !== undefined && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json"
  }

  const method = (init?.method ?? "GET").toUpperCase()
  const startedAt = Date.now()
  logHttp(`${method} ${path}`)

  const response = await authenticatedFetch(url, { ...init, headers, credentials: init?.credentials ?? "include" })
  if (!response.ok) {
    const message = await readErrorMessage(response)
    logHttp(`${method} ${path} -> ${response.status}`, { durationMs: Date.now() - startedAt, error: message })
    throw new Error(message || `Request failed with ${response.status}`)
  }

  logHttp(`${method} ${path} -> ${response.status}`, { durationMs: Date.now() - startedAt })
  return response
}


export const serverApi = {
  fetchPermissionReceipts(instanceId: string, sessionId: string, scope: { messageId: string } | { unanchored: true }, cursor?: string, signal?: AbortSignal): Promise<PermissionReceiptPage> {
    const params = new URLSearchParams("messageId" in scope ? { messageId: scope.messageId } : { unanchored: "true" })
    if (cursor) params.set("cursor", cursor)
    return request(`/api/workspaces/${encodeURIComponent(instanceId)}/sessions/${encodeURIComponent(sessionId)}/permission-receipts?${params}`, { signal })
  },
  fetchHistoryWindow(instanceId: string, sessionID: string, target: NavigationTarget, signal?: AbortSignal): Promise<NavigationWindowResult> {
    return request(`/api/workspaces/${encodeURIComponent(instanceId)}/session-history/window`, {
      method: "POST", body: JSON.stringify({ sessionID, target }), signal,
    })
  },
  fetchSessionOutline(instanceId: string, sessionID: string, cursor?: { after: number; through: number }, signal?: AbortSignal, after?: number, known?: OutlineCheckpoint[]): Promise<OutlineResult> {
    return request(`/api/workspaces/${encodeURIComponent(instanceId)}/session-history/outline`, {
      method: "POST", body: JSON.stringify({ sessionID, cursor, after, known }), signal,
    })
  },
  fetchOutlinePreviews(instanceId: string, sessionID: string, messageIDs: string[], signal?: AbortSignal): Promise<OutlinePreviewResult> {
    return request(`/api/workspaces/${encodeURIComponent(instanceId)}/session-history/outlinePreview`, {
      method: "POST", body: JSON.stringify({ sessionID, messageIDs }), signal,
    })
  },
  querySessionHistory(instanceId: string, input: HistoryQuery, signal?: AbortSignal): Promise<HistoryResult> {
    return request(`/api/workspaces/${encodeURIComponent(instanceId)}/session-history/query`, {
      method: "POST", body: JSON.stringify(input), signal,
    })
  },
  pruneSessionHistory(instanceId: string, input: PruneBatch, signal?: AbortSignal): Promise<PruneBatchResult> {
    return request(`/api/workspaces/${encodeURIComponent(instanceId)}/session-history/prune`, {
      method: "POST", body: JSON.stringify(input), signal,
    })
  },
  pruneSessionMessage(instanceId: string, input: PruneRequest): Promise<PruneResult> {
    return request(`/api/workspaces/${encodeURIComponent(instanceId)}/session-pruning/prune`, {
      method: "POST", body: JSON.stringify(input),
    })
  },
  fetchWorkspaces(): Promise<WorkspaceDescriptor[]> {
    return request<WorkspaceDescriptor[]>("/api/workspaces")
  },

  fetchProviderUsage(instanceId: string, sessionId: string, providerId: string, modelId?: string): Promise<ProviderUsageResponse> {
    const params = new URLSearchParams({ instanceId, sessionId })
    if (modelId) params.set("modelId", modelId)
    const query = params.toString()
    return request<ProviderUsageResponse>(`/api/usage/${encodeURIComponent(providerId)}${query ? `?${query}` : ""}`)
  },

  fetchWorktrees(id: string): Promise<WorktreeListResponse> {
    return request<WorktreeListResponse>(`/api/workspaces/${encodeURIComponent(id)}/worktrees`)
  },

  createWorktree(id: string, payload: WorktreeCreateRequest): Promise<{ slug: string; directory: string; branch?: string }> {
    return request<{ slug: string; directory: string; branch?: string }>(`/api/workspaces/${encodeURIComponent(id)}/worktrees`, {
      method: "POST",
      body: JSON.stringify(payload),
    })
  },

  deleteWorktree(id: string, slug: string, options?: { force?: boolean }): Promise<void> {
    const params = new URLSearchParams()
    if (options?.force) {
      params.set("force", "true")
    }
    const suffix = params.toString() ? `?${params.toString()}` : ""
    return request(`/api/workspaces/${encodeURIComponent(id)}/worktrees/${encodeURIComponent(slug)}${suffix}`, {
      method: "DELETE",
    })
  },

  moveSessionFamily(id: string, sessionId: string, payload: WorktreeSessionMoveRequest): Promise<WorktreeSessionMoveResponse> {
    return request<WorktreeSessionMoveResponse>(`/api/workspaces/${encodeURIComponent(id)}/sessions/${encodeURIComponent(sessionId)}/worktree`, {
      method: "POST",
      body: JSON.stringify(payload),
    })
  },

  createWorkspace(payload: WorkspaceCreateRequest, options?: { signal?: AbortSignal }): Promise<WorkspaceCreateResponse> {
    return request<WorkspaceCreateResponse>("/api/workspaces", {
      method: "POST",
      body: JSON.stringify(payload),
      signal: options?.signal,
    })
  },
  cancelWorkspaceCreation(requestId: string): Promise<void> {
    return request("/api/workspaces/creation/cancel", {
      method: "POST",
      body: JSON.stringify({ requestId }),
    })
  },
  releaseWorkspaceCreation(id: string, requestId: string): Promise<void> {
    return request(`/api/workspaces/${encodeURIComponent(id)}/creation/release`, {
      method: "POST",
      body: JSON.stringify({ requestId }),
    })
  },
  fetchSidecars(): Promise<{ sidecars: SideCar[] }> {
    return request<{ sidecars: SideCar[] }>("/api/sidecars")
  },
  createSidecar(payload: {
    kind: "port"
    name: string
    port: number
    insecure: boolean
    prefixMode: "strip" | "preserve"
  }): Promise<SideCar> {
    return request<SideCar>("/api/sidecars", {
      method: "POST",
      body: JSON.stringify(payload),
    })
  },
  updateSidecar(
    id: string,
    payload: Partial<{ name: string; port: number; insecure: boolean; prefixMode: "strip" | "preserve" }>,
  ): Promise<SideCar> {
    return request<SideCar>(`/api/sidecars/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    })
  },
  deleteSidecar(id: string): Promise<void> {
    return request(`/api/sidecars/${encodeURIComponent(id)}`, { method: "DELETE" })
  },
  createPreview(payload: { sessionId: string; url: string }): Promise<PreviewSession> {
    return request<PreviewSession>("/api/previews", {
      method: "POST",
      body: JSON.stringify(payload),
    })
  },
  deletePreview(token: string): Promise<void> {
    return request(`/api/previews/${encodeURIComponent(token)}`, { method: "DELETE" })
  },
  fetchServerMeta(): Promise<ServerMeta> {
    return request<ServerMeta>("/api/meta")
  },
  probeRemoteServer(payload: RemoteServerProbeRequest): Promise<RemoteServerProbeResponse> {
    return request<RemoteServerProbeResponse>("/api/remote-servers/probe", {
      method: "POST",
      body: JSON.stringify(payload),
    })
  },
  createRemoteProxySession(payload: RemoteProxySessionCreateRequest): Promise<RemoteProxySessionCreateResponse> {
    return request<RemoteProxySessionCreateResponse>("/api/remote-proxy/sessions", {
      method: "POST",
      body: JSON.stringify(payload),
    })
  },
  deleteRemoteProxySession(id: string): Promise<void> {
    return request(`/api/remote-proxy/sessions/${encodeURIComponent(id)}`, { method: "DELETE" })
  },
  fetchAuthStatus(): Promise<{ authenticated: boolean; username?: string; passwordUserProvided?: boolean }> {
    return request<{ authenticated: boolean; username?: string; passwordUserProvided?: boolean }>("/api/auth/status")
  },
  listConfigFiles(): Promise<ConfigFileListResponse> {
    return request<ConfigFileListResponse>("/api/config-files")
  },
  readConfigFile(id: string): Promise<ConfigFileContentResponse> {
    return request<ConfigFileContentResponse>(`/api/config-files/${encodeURIComponent(id)}/content`)
  },
  writeConfigFile(id: string, contents: string): Promise<void> {
    const body: ConfigFileContentRequest = { contents }
    return request(`/api/config-files/${encodeURIComponent(id)}/content`, {
      method: "PUT",
      body: JSON.stringify(body),
    })
  },
  getPluginControls(instanceId: string, location: PluginControlLocation, signal?: AbortSignal): Promise<PluginControlsSnapshot> {
    const params = new URLSearchParams({ directory: location.directory })
    if (location.workspaceID) params.set("workspaceID", location.workspaceID)
    return request<PluginControlsSnapshot>(`/api/workspaces/${encodeURIComponent(instanceId)}/plugin-controls?${params.toString()}`, { signal })
  },
  getWebSearchSettings(instanceId: string, directory: string): Promise<import("../../../server/src/api-types").WebSearchSettingsSnapshot> {
    return request(`/api/workspaces/${encodeURIComponent(instanceId)}/websearch-settings?${new URLSearchParams({ directory })}`)
  },
  setWebSearchSettings(instanceId: string, payload: import("../../../server/src/api-types").WebSearchSettingsMutation): Promise<void> {
    return request(`/api/workspaces/${encodeURIComponent(instanceId)}/websearch-settings`, { method: "PUT", body: JSON.stringify(payload) })
  },
  setPluginActivation(instanceId: string, payload: PluginActivationMutationRequest): Promise<PluginActivationMutationResponse> {
    return request<PluginActivationMutationResponse>(`/api/workspaces/${encodeURIComponent(instanceId)}/plugin-controls`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    })
  },
  setServerPassword(password: string): Promise<{ ok: boolean; username: string; passwordUserProvided: boolean }> {
    return request<{ ok: boolean; username: string; passwordUserProvided: boolean }>("/api/auth/password", {
      method: "POST",
      body: JSON.stringify({ password }),
    })
  },
  deleteWorkspace(id: string): Promise<void> {
    return request(`/api/workspaces/${encodeURIComponent(id)}`, { method: "DELETE" })
  },
  getPendingRequests(id: string, directories: string[], signal?: AbortSignal): Promise<WorkspacePendingRequestsResponse> {
    const query = new URLSearchParams(directories.map((directory) => ["directories", directory]))
    return request(`/api/workspaces/${encodeURIComponent(id)}/pending-requests?${query}`, { signal })
  },
  cloneWorkspaceRepository(payload: WorkspaceCloneRequest): Promise<WorkspaceCloneResponse> {
    return request<WorkspaceCloneResponse>("/api/workspaces/clone", {
      method: "POST",
      body: JSON.stringify(payload),
    })
  },
  listWorkspaceFiles(id: string, relativePath = ".", directory?: string, signal?: AbortSignal): Promise<FileSystemEntry[]> {
    const params = new URLSearchParams({ path: relativePath })
    if (directory) params.set("directory", directory)
    return request<FileSystemEntry[]>(`/api/workspaces/${encodeURIComponent(id)}/files?${params.toString()}`, { signal })
  },
  searchWorkspaceFiles(
    id: string,
    query: string,
    opts?: { limit?: number; type?: "file" | "directory" | "all"; signal?: AbortSignal; directory?: string },
  ): Promise<WorkspaceFileSearchResponse> {
    const trimmed = query.trim()
    if (!trimmed) {
      return Promise.resolve([])
    }
    const params = new URLSearchParams({ q: trimmed })
    if (opts?.directory) params.set("directory", opts.directory)
    if (opts?.limit) {
      params.set("limit", String(opts.limit))
    }
    if (opts?.type) {
      params.set("type", opts.type)
    }
    const search = () => request<WorkspaceFileSearchResponse>(
      `/api/workspaces/${encodeURIComponent(id)}/files/search?${params.toString()}`,
      { signal: opts?.signal },
    )
    return opts?.signal ? retryFileSearch(search, opts.signal) : search()
  },
  previewWorkspaceFile(id: string, relativePath: string, directory: string, signal?: AbortSignal): Promise<WorkspaceFileResponse> {
    const params = new URLSearchParams({ path: relativePath, directory })
    return request(`/api/workspaces/${encodeURIComponent(id)}/files/preview?${params}`, { signal })
  },
  readWorkspaceFile(id: string, relativePath: string, options?: { encoding?: "utf-8" | "base64" }): Promise<WorkspaceFileResponse> {
    const params = new URLSearchParams({ path: relativePath })
    if (options?.encoding) {
      params.set("encoding", options.encoding)
    }
    return request<WorkspaceFileResponse>(
      `/api/workspaces/${encodeURIComponent(id)}/files/content?${params.toString()}`,
    )
  },
  writeWorkspaceFile(id: string, relativePath: string, contents: string, options?: { worktree?: string; directory?: string }): Promise<void> {
    const params = new URLSearchParams({ path: relativePath })
    if (options?.directory) params.set("directory", options.directory)
    if (options?.worktree && options.worktree !== "root") {
      params.set("worktree", options.worktree)
    }
    return request(
      `/api/workspaces/${encodeURIComponent(id)}/files/content?${params.toString()}`,
      {
        method: "PUT",
        body: JSON.stringify({ contents }),
      },
    )
  },
  fetchWorktreeGitStatus(id: string, slug: string, signal?: AbortSignal): Promise<WorktreeGitStatusResponse> {
    return request<WorktreeGitStatusResponse>(
      `/api/workspaces/${encodeURIComponent(id)}/worktrees/${encodeURIComponent(slug)}/git-status`,
      { signal },
    )
  },
  fetchGitHistory(id: string, slug: string, offset = 0, head?: string, signal?: AbortSignal): Promise<GitHistoryPage> {
    const query = new URLSearchParams({ offset: String(offset), ...(head ? { head } : {}) })
    return request(`/api/workspaces/${encodeURIComponent(id)}/worktrees/${encodeURIComponent(slug)}/git-history?${query}`, { signal })
  },
  fetchGitCommit(id: string, slug: string, commit: string, signal?: AbortSignal): Promise<GitCommitDetails> {
    return request(`/api/workspaces/${encodeURIComponent(id)}/worktrees/${encodeURIComponent(slug)}/git-history/${encodeURIComponent(commit)}`, { signal })
  },
  fetchGitCommitDiff(id: string, slug: string, commit: string, path: string, signal?: AbortSignal): Promise<GitCommitDiff> {
    return request(`/api/workspaces/${encodeURIComponent(id)}/worktrees/${encodeURIComponent(slug)}/git-history/${encodeURIComponent(commit)}?${new URLSearchParams({ path })}`, { signal })
  },
  fetchWorktreeGitDiff(id: string, slug: string, requestPayload: WorktreeGitDiffRequest, signal?: AbortSignal): Promise<WorktreeGitDiffResponse> {
    const params = new URLSearchParams({ path: requestPayload.path, scope: requestPayload.scope })
    if (requestPayload.originalPath) {
      params.set("originalPath", requestPayload.originalPath)
    }
    return request<WorktreeGitDiffResponse>(
      `/api/workspaces/${encodeURIComponent(id)}/worktrees/${encodeURIComponent(slug)}/git-diff?${params.toString()}`,
      { signal },
    )
  },
  stageWorktreeGitPaths(id: string, slug: string, payload: WorktreeGitPathsRequest): Promise<WorktreeGitMutationResponse> {
    return request<WorktreeGitMutationResponse>(
      `/api/workspaces/${encodeURIComponent(id)}/worktrees/${encodeURIComponent(slug)}/git-stage`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    )
  },
  unstageWorktreeGitPaths(id: string, slug: string, payload: WorktreeGitPathsRequest): Promise<WorktreeGitMutationResponse> {
    return request<WorktreeGitMutationResponse>(
      `/api/workspaces/${encodeURIComponent(id)}/worktrees/${encodeURIComponent(slug)}/git-unstage`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    )
  },
  commitWorktreeGitChanges(id: string, slug: string, payload: WorktreeGitCommitRequest): Promise<WorktreeGitCommitResponse> {
    return request<WorktreeGitCommitResponse>(
      `/api/workspaces/${encodeURIComponent(id)}/worktrees/${encodeURIComponent(slug)}/git-commit`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    )
  },

  getProviderAccounts(instanceId: string, integrationID: string, directory: string): Promise<ProviderAccountsSnapshot> {
    return request(`/api/workspaces/${encodeURIComponent(instanceId)}/provider-accounts/${encodeURIComponent(integrationID)}?${new URLSearchParams({ directory })}`)
  },
  setProviderAccountSelection(instanceId: string, integrationID: string, directory: string, enabled: boolean): Promise<ProviderAccountsSnapshot> {
    return request(`/api/workspaces/${encodeURIComponent(instanceId)}/provider-accounts/${encodeURIComponent(integrationID)}`, {
      method: "PUT", body: JSON.stringify({ directory, enabled }),
    })
  },
  fetchConfigOwner<T extends Record<string, any> = Record<string, any>>(owner: string): Promise<T> {
    return request<T>(`/api/storage/config/${encodeURIComponent(owner)}`)
  },
  patchConfigOwner<T extends Record<string, any> = Record<string, any>>(owner: string, patch: unknown): Promise<T> {
    return request<T>(`/api/storage/config/${encodeURIComponent(owner)}`, {
      method: "PATCH",
      body: JSON.stringify(patch ?? {}),
    })
  },
  fetchStateOwner<T extends Record<string, any> = Record<string, any>>(owner: string): Promise<T> {
    return request<T>(`/api/storage/state/${encodeURIComponent(owner)}`)
  },
  patchStateOwner<T extends Record<string, any> = Record<string, any>>(owner: string, patch: unknown): Promise<T> {
    return request<T>(`/api/storage/state/${encodeURIComponent(owner)}`, {
      method: "PATCH",
      body: JSON.stringify(patch ?? {}),
    })
  },

  validateBinary(path: string): Promise<BinaryValidationResult> {
    return request<BinaryValidationResult>("/api/storage/binaries/validate", {
      method: "POST",
      body: JSON.stringify({ path }),
    })
  },
  fetchOpenCodeUpdateStatus(): Promise<OpenCodeUpdateStatus> {
    return request<OpenCodeUpdateStatus>("/api/opencode/update")
  },
  updateOpenCode(): Promise<OpenCodeUpdateResponse> {
    return request<OpenCodeUpdateResponse>("/api/opencode/update", { method: "POST" })
  },
  startOpenCode(restart = false): Promise<OpenCodeUpdateStatus> {
    return request<OpenCodeUpdateStatus>("/api/opencode/service", { method: "POST", body: JSON.stringify({ restart }) })
  },
  reloadOpenCodeConfiguration(): Promise<OpenCodeUpdateStatus> {
    return request<OpenCodeUpdateStatus>("/api/opencode/service", { method: "POST", body: JSON.stringify({ reload: true }) })
  },
  fetchSpeechCapabilities(): Promise<SpeechCapabilitiesResponse> {
    return request<SpeechCapabilitiesResponse>("/api/speech/capabilities")
  },
  transcribeAudio(payload: {
    audioBase64: string
    mimeType: string
    filename?: string
    language?: string
    prompt?: string
  }): Promise<SpeechTranscriptionResponse> {
    return request<SpeechTranscriptionResponse>("/api/speech/transcribe", {
      method: "POST",
      body: JSON.stringify(payload),
    })
  },
  synthesizeSpeech(payload: { text: string; format?: "mp3" | "wav" | "opus" | "aac" }): Promise<SpeechSynthesisResponse> {
    return request<SpeechSynthesisResponse>("/api/speech/synthesize", {
      method: "POST",
      body: JSON.stringify(payload),
    })
  },
  synthesizeSpeechStream(
    payload: { text: string; format?: "mp3" | "wav" | "opus" | "aac" },
    signal?: AbortSignal,
  ): Promise<Response> {
    return requestRaw("/api/speech/synthesize/stream", {
      method: "POST",
      body: JSON.stringify(payload),
      signal,
    })
  },
  listFileSystem(path?: string, options?: { includeFiles?: boolean }): Promise<FileSystemListResponse> {
    const params = new URLSearchParams()
    if (path && path !== ".") {
      params.set("path", path)
    }
    if (options?.includeFiles !== undefined) {
      params.set("includeFiles", String(options.includeFiles))
    }
    const query = params.toString()
    return request<FileSystemListResponse>(query ? `/api/filesystem?${query}` : "/api/filesystem")
  },

  createFileSystemFolder(parentPath: string | undefined, name: string): Promise<FileSystemCreateFolderResponse> {
    return request<FileSystemCreateFolderResponse>("/api/filesystem/folders", {
      method: "POST",
      body: JSON.stringify({ parentPath, name }),
    })
  },
  readFileSystemFile(path: string, options?: { encoding?: "utf-8" | "base64" }): Promise<FileSystemFileContentResponse> {
    const params = new URLSearchParams({ path })
    if (options?.encoding) {
      params.set("encoding", options.encoding)
    }
    return request<FileSystemFileContentResponse>(`/api/filesystem/files/content?${params.toString()}`)
  },
  readInstanceData(id: string): Promise<InstanceData> {
    return request<InstanceData>(`/api/storage/instances/${encodeURIComponent(id)}`)
  },
  writeInstanceData(id: string, data: InstanceData): Promise<void> {
    return request(`/api/storage/instances/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  },
  deleteInstanceData(id: string): Promise<void> {
    return request(`/api/storage/instances/${encodeURIComponent(id)}`, { method: "DELETE" })
  },
  getYoloState(instanceId: string, sessionId: string): Promise<YoloStateResponse> {
    return request<YoloStateResponse>(
      `/workspaces/${encodeURIComponent(instanceId)}/yolo/sessions/${encodeURIComponent(sessionId)}`,
    )
  },
  toggleYolo(instanceId: string, sessionId: string): Promise<YoloStateResponse> {
    return request<YoloStateResponse>(
      `/workspaces/${encodeURIComponent(instanceId)}/yolo/sessions/${encodeURIComponent(sessionId)}/toggle`,
      { method: "POST" },
    )
  },
  sendClientConnectionPong(payload: { clientId: string; connectionId: string; pingTs?: number }, signal?: AbortSignal): Promise<void> {
    const init: RequestInit = {
      method: "POST",
      body: JSON.stringify(payload),
    }
    if (signal) {
      init.signal = signal
    }
    return request<void>("/api/client-connections/pong", init)
  },
  connectEvents(
    onEvent: (event: WorkspaceEventPayload) => void,
    onError?: () => void,
    onPing?: (payload: { ts?: number }) => void,
  ) {
    const identity = getClientIdentity()
    const url = buildClientEventsUrl(identity)
    sseLogger.info(`Connecting to ${url}`)
    const source = new EventSource(url, { withCredentials: true } as any)
    attachEventSourceHandlers(source, { onEvent, onError, onPing, logger: sseLogger })
    return source
  },
}

function buildClientEventsUrl(identity: { clientId: string; connectionId: string }): string {
  const url = new URL(EVENTS_URL, typeof window !== "undefined" ? window.location.origin : "http://localhost")
  url.searchParams.set("clientId", identity.clientId)
  url.searchParams.set("connectionId", identity.connectionId)
  if (EVENTS_URL.startsWith("http://") || EVENTS_URL.startsWith("https://")) {
    return url.toString()
  }
  return `${url.pathname}${url.search}`
}

export type { WorkspaceDescriptor, WorkspaceLogEntry, WorkspaceEventPayload, WorkspaceEventType, SideCar }
